import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AddIcon from "@mui/icons-material/Add";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import type { Identifier, XYCoord } from "dnd-core";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import CustomModal from "../../../../components/common/custom-modal/custom-modal";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  getCategoryAction,
  getSubCategoryAction,
  getPricing,
  getAlterationSubCategories,
  getCategoryMaterialRelationship,
  getCategoryRelationship,
  reorderCategoriesAction,
  reorderSubCategoriesAction,
  createSubcategoryWithImageAction,
  createMaterialAction,
  reorderMaterialsAction,
  createSecondSubcategoryAction,
  reorderSecondSubcategoriesAction,
  updateCategoryAction,
  updateSubcategoryAction,
  updateMaterialAction,
  updateSecondSubcategoryAction,
  upsertPriceAction,
  resetListPriceAction,
  patchCategoryAction,
  deleteCategoryAction,
  deleteSubcategoryAction,
  deleteMaterialAction,
  deleteSecondSubcategoryAction,
} from "../../../../redux/actions/pricingAction";
import PriceListSelect from "../../../../components/common/price-list-select/PriceListSelect";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import axios from "axios";
import { GET_MENU_USAGE } from "../../../../configs/apiConfig";
import { hasNoUsage, sortByName, sortByUsage } from "../../../../util/menu-sort";
import { SubcategoryResponse } from "../../../../typings/interfaces/responses/sub-category-response";
import { CategoryResponse } from "../../../../typings/interfaces";
import { CategoryMaterialRelationshipResponse } from "../../../../typings/interfaces/responses/category-material-relationship";
import { CategoryRelationshipResponse } from "../../../../typings/interfaces/responses/category-relationship-response";

const ITEM_TYPE_SERVICE = "SERVICE_ITEM";
const ITEM_TYPE_GARMENT = "GARMENT_ITEM";
const ITEM_TYPE_MATERIAL = "MATERIAL_ITEM";
const ITEM_TYPE_SUBTYPE = "SUBTYPE_ITEM";

// ─── Shared styles ────────────────────────────────────────────────────────────

const COLUMN_BG = "#F4F4F4";
const ROW_SELECTED_BG = "#FFFFFF";
const ROW_DEFAULT_BG = "transparent";
const PRICE_BOX_BG = "#EFEFEF";
const PRICE_BOX_BORDER = "#E0E0E0";

// Green MUI Switch styled to match Figma
const GreenSwitch = (props: { checked: boolean; size?: "small" | "medium" }) => (
  <Switch
    checked={props.checked}
    size={props.size ?? "small"}
    sx={{
      "& .MuiSwitch-switchBase.Mui-checked": { color: Colors.PRIMARY },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: Colors.PRIMARY,
      },
      pointerEvents: "none",
    }}
  />
);

// ─── Drag item type ───────────────────────────────────────────────────────────

interface DragItem {
  index: number;
  id: number;
  type: string;
}

// ─── Generic drag hook ────────────────────────────────────────────────────────

function useDragDrop(
  itemType: string,
  id: number,
  index: number,
  moveItem: (drag: number, hover: number) => void
) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: itemType,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const midY = (rect.bottom - rect.top) / 2;
      const clientY = (monitor.getClientOffset() as XYCoord).y - rect.top;
      if (dragIndex < hoverIndex && clientY < midY) return;
      if (dragIndex > hoverIndex && clientY > midY) return;
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemType,
    item: () => ({ id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));
  return { ref, handlerId, isDragging };
}

// ─── Draggable Service Row ────────────────────────────────────────────────────

interface DraggableServiceButtonProps {
  id: number;
  index: number;
  label: string;
  selected: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
}

const DraggableServiceButton: FC<DraggableServiceButtonProps> = ({
  id, index, label, selected, onClick, onEdit, onDelete, moveItem,
}) => {
  const { ref, handlerId, isDragging } = useDragDrop(ITEM_TYPE_SERVICE, id, index, moveItem);

  return (
    <Box
      ref={ref}
      data-handler-id={handlerId}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        px: "0.75rem",
        height: "2.75rem",
        flexShrink: 0,
        borderRadius: "0.3rem",
        cursor: isDragging ? "grabbing" : "pointer",
        opacity: isDragging ? 0.4 : 1,
        background: selected ? ROW_SELECTED_BG : ROW_DEFAULT_BG,
        boxShadow: selected ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
        userSelect: "none",
        transition: "background 0.12s, box-shadow 0.12s",
        "&:hover .row-actions": { opacity: 1 },
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: "1rem",
          fontWeight: 500,
          color: "#515151",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box className="row-actions" sx={{ display: "flex", gap: "0.15rem", opacity: 0, transition: "opacity 0.15s" }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }} sx={{ p: "0.2rem" }}>
            <EditOutlinedIcon sx={{ fontSize: "0.95rem", color: "#515151" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ p: "0.2rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "0.95rem", color: "#515151" }} />
          </IconButton>
        </Tooltip>
      </Box>
      <DragHandleIcon
        sx={{ fontSize: "1.2rem", color: "#B3B3B3", cursor: "grab", flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      />
    </Box>
  );
};

// ─── Draggable Item Row (Garment / SubType / Material) ────────────────────────

interface DraggableItemRowProps {
  id: number;
  index: number;
  label: string;
  selected: boolean;
  toggleOn: boolean;
  price: number | null;
  showPrice: boolean;
  itemType: string;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
}

const DraggableItemRow: FC<DraggableItemRowProps> = ({
  id, index, label, selected, toggleOn, price, showPrice,
  itemType, onClick, onEdit, onDelete, moveItem,
}) => {
  const { ref, handlerId, isDragging } = useDragDrop(itemType, id, index, moveItem);
  const isActive = toggleOn;

  return (
    <Box
      ref={ref}
      data-handler-id={handlerId}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        px: "0.6rem",
        height: "2.75rem",
        flexShrink: 0,
        borderRadius: "0.3rem",
        cursor: isDragging ? "grabbing" : "pointer",
        opacity: isDragging ? 0.4 : 1,
        background: selected ? ROW_SELECTED_BG : ROW_DEFAULT_BG,
        boxShadow: selected ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
        userSelect: "none",
        transition: "background 0.12s, box-shadow 0.12s",
        "&:hover .row-actions": { opacity: 1 },
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: "0.95rem",
          fontWeight: 500,
          color: isActive ? "#515151" : Colors.ACCENT_GRAY_DISABLED,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
      <GreenSwitch checked={isActive} />
      {showPrice && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.2rem",
            background: PRICE_BOX_BG,
            border: `1px solid ${PRICE_BOX_BORDER}`,
            borderRadius: "0.25rem",
            px: "0.5rem",
            height: "1.8rem",
            minWidth: "4.8rem",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "0.8rem", color: isActive ? "#515151" : Colors.ACCENT_GRAY_DISABLED, fontWeight: 400 }}>
            $
          </Typography>
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: isActive ? "#515151" : Colors.ACCENT_GRAY_DISABLED,
              fontWeight: 400,
              flex: 1,
              textAlign: "right",
            }}
          >
            {price !== null ? price.toFixed(2) : "0.00"}
          </Typography>
        </Box>
      )}
      <Box className="row-actions" sx={{ display: "flex", gap: "0.1rem", opacity: 0, transition: "opacity 0.15s", flexShrink: 0 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }} sx={{ p: "0.15rem" }}>
            <EditOutlinedIcon sx={{ fontSize: "0.85rem", color: "#515151" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ p: "0.15rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "0.85rem", color: "#515151" }} />
          </IconButton>
        </Tooltip>
      </Box>
      <DragHandleIcon
        sx={{ fontSize: "1.2rem", color: "#B3B3B3", cursor: "grab", flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      />
    </Box>
  );
};

// ─── Column wrapper ───────────────────────────────────────────────────────────

interface ColumnProps {
  title: string;
  onAdd?: (() => void) | null;
  addDisabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  onSortAZ?: () => void;
  onSortMostUsed?: () => void;
  sortDisabled?: boolean;
}

const Column: FC<ColumnProps> = ({
  title, onAdd, addDisabled, loading, children, onSortAZ, onSortMostUsed, sortDisabled,
}) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: COLUMN_BG,
      borderRadius: "0.75rem",
      p: "1.25rem",
      minWidth: 0,
      minHeight: 0,
    }}
  >
    {/* Column header */}
    <Box sx={{ display: "flex", alignItems: "center", mb: "1rem", flexShrink: 0, gap: "0.5rem" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#515151", flex: 1 }}>
        {title}
      </Typography>
      {/* One-off sorts; drag still works after, then Save */}
      {onSortAZ && (
        <Tooltip title={`Sort ${title} A–Z`}>
          <span>
            <Button
              size="small"
              aria-label={`Sort ${title} A to Z`}
              disabled={sortDisabled}
              onClick={onSortAZ}
              sx={{ minWidth: 0, px: "0.4rem", fontSize: "var(--fs-2xs)", textTransform: "none", color: Colors.PRIMARY }}
            >
              A–Z
            </Button>
          </span>
        </Tooltip>
      )}
      {onSortMostUsed && (
        <Tooltip title={`Sort ${title} by most used (last 90 days)`}>
          <span>
            <Button
              size="small"
              aria-label={`Sort ${title} by most used`}
              disabled={sortDisabled}
              onClick={onSortMostUsed}
              sx={{ minWidth: 0, px: "0.4rem", fontSize: "var(--fs-2xs)", textTransform: "none", color: Colors.PRIMARY }}
            >
              Most used
            </Button>
          </span>
        </Tooltip>
      )}
      {onAdd && (
        <Tooltip title={`Add ${title}`}>
          <span>
            <IconButton
              size="small"
              disabled={addDisabled}
              onClick={onAdd}
              sx={{
                background: addDisabled ? Colors.ACCENT_GRAY_DISABLED : Colors.PRIMARY,
                color: Colors.WHITE,
                width: "1.6rem",
                height: "1.6rem",
                borderRadius: "0.3rem",
                "&:hover": { background: addDisabled ? Colors.ACCENT_GRAY_DISABLED : Colors.PRIMARY_DARK },
                "&.Mui-disabled": { background: Colors.ACCENT_GRAY_DISABLED, color: Colors.WHITE },
              }}
            >
              <AddIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>

    {/* Column body */}
    {loading ? (
      <Box sx={{ display: "flex", justifyContent: "center", pt: "2rem", flex: 1 }}>
        <CircularProgress size={28} />
      </Box>
    ) : (
      <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.35rem", minHeight: 0 }}>
        {children}
      </Box>
    )}

  </Box>
);

// ─── Page ────────────────────────────────────────────────────────────────────

export const PriceSetupPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();

  const {
    categoryResponse,
    categoryResponseLoading,
    subCategoryResponse,
    subCategoryResponseLoading,
    dryCleanResponse,
    dryCleanResponseLoading,
    alterationSubCategoryResponse,
    alterationSubCategoryResponseLoading,
    categoryMaterialRelationship,
    categoryMaterialRelationshipLoading,
    categoryRelationshipResponse,
    reorderCategoriesLoading,
    reorderSubCategoriesLoading,
    createSubcategoryLoading,
    createMaterialLoading,
    reorderMaterialsLoading,
    createSecondSubcategoryLoading,
    reorderSecondSubcategoriesLoading,
    updateCategoryLoading,
    updateSubcategoryLoading,
    updateMaterialLoading,
    updateSecondSubcategoryLoading,
    upsertPriceLoading,
    deleteCategoryLoading,
    deleteSubcategoryLoading,
    deleteMaterialLoading,
    deleteSecondSubcategoryLoading,
  } = useAppSelector((state) => state.pricing);

  // Garment modal state
  const [addGarmentModalOpen, setAddGarmentModalOpen] = useState(false);
  const [newGarmentName, setNewGarmentName] = useState("");
  const [newGarmentFile, setNewGarmentFile] = useState<File | null>(null);

  // Material modal state
  const [addMaterialModalOpen, setAddMaterialModalOpen] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState("");

  // Sub Type modal state
  const [addSubTypeModalOpen, setAddSubTypeModalOpen] = useState(false);
  const [newSubTypeName, setNewSubTypeName] = useState("");
  const [newSubTypePrice, setNewSubTypePrice] = useState<string>("");
  const [newSubTypePieceCount, setNewSubTypePieceCount] = useState<string>("1");

  // Delete confirm state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: "service" | "garment" | "material" | "subtype";
    id: number;
    name: string;
  } | null>(null);

  // Unsaved order changes tracking
  const [hasChanges, setHasChanges] = useState(false);
  const [navConfirmOpen, setNavConfirmOpen] = useState(false);

  // Edit state
  const [editService, setEditService] = useState<{
    id: number;
    name: string;
    code: string;
    showInMenu: boolean;
    alterationCategoryId: number | null;
    // Text so the field can be cleared; blank = use the merchant default
    maxItemsPerBill: string;
    allowZeroPrice: boolean;
  } | null>(null);
  const [editGarment, setEditGarment] = useState<{ id: number; name: string; categoryId: number } | null>(null);
  const [editMaterial, setEditMaterial] = useState<{ id: number; name: string } | null>(null);
  const [editSubType, setEditSubType] = useState<{ id: number; name: string; price: string; pieceCount: string } | null>(null);

  // Every service, including ones hidden from the POS, so they can be switched
  // back on. Alteration categories are configured through their service.
  const allCategories = categoryResponse?.body ?? [];
  const isAlterationCategory = (c: CategoryResponse) =>
    /alteration/i.test(c.categoryName) || allCategories.some((s) => s.alterationCategoryId === c.id);
  const categoryList = allCategories.filter((c) => !isAlterationCategory(c));
  const alterationCategoryOptions = allCategories.filter(isAlterationCategory);
  const alterationCategory = { id: -100000, categoryName: "Alteration" } as CategoryResponse & { id: number };
  const displayCategories = [...categoryList, alterationCategory];

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  // Price list being edited; prices it doesn't have show the default's price
  const [priceListId, setPriceListId] = useState<number | null>(null);
  const { priceLists } = useAppSelector((state) => state.priceLists);
  const defaultMaxItemsPerBill = useAppSelector(
    (state) => state.merchant.merchantListResponse?.body?.[0]?.defaultMaxItemsPerBill
  );
  const editServiceMaxItems = editService?.maxItemsPerBill.trim() ?? "";
  const editServiceMaxItemsInvalid =
    editServiceMaxItems !== "" && !(/^\d+$/.test(editServiceMaxItems) && Number(editServiceMaxItems) >= 1);
  const defaultPriceListId = priceLists.find((list) => list.isDefault)?.id ?? null;
  const editingDefaultList = priceListId === null || priceListId === defaultPriceListId;
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);

  // Local ordered lists for DnD
  const [orderedCategories, setOrderedCategories] = useState<typeof displayCategories>([]);
  const [orderedSubcategories, setOrderedSubcategories] = useState<SubcategoryResponse[]>([]);
  const [orderedMaterials, setOrderedMaterials] = useState<CategoryMaterialRelationshipResponse[]>([]);
  const [orderedSubTypes, setOrderedSubTypes] = useState<CategoryRelationshipResponse[]>([]);
  // Pieces checked in per garment / fabric / style ("garmentId:styleId")
  // for the selected service over the last 90 days, for "Most used"
  const [usage, setUsage] = useState<{
    garments: Record<string, number>;
    fabrics: Record<string, number>;
    styles: Record<string, number>;
  } | null>(null);

  // Fetch categories + material relationships + category relationships on mount
  useEffect(() => {
    dispatch(getCategoryAction());
    dispatch(getCategoryMaterialRelationship());
    dispatch(getCategoryRelationship());
  }, [dispatch]);

  // Sync ordered categories when Redux data changes
  useEffect(() => {
    setOrderedCategories(displayCategories);
  }, [categoryResponse]);

  // Auto-select first category once loaded
  useEffect(() => {
    if (!categoryResponseLoading && selectedCategoryId === null && categoryList.length > 0) {
      setSelectedCategoryId(categoryList[0].id);
    }
  }, [categoryResponseLoading, categoryList, selectedCategoryId]);

  // Fetch subcategories + prices when selected category changes
  useEffect(() => {
    if (selectedCategoryId === null) return;
    setSelectedSubcategoryId(null);
    setSelectedMaterialId(null);
    if (selectedCategoryId === -100000) {
      dispatch(getAlterationSubCategories());
    } else {
      dispatch(getSubCategoryAction({ id: selectedCategoryId }));
      dispatch(getPricing({ id: selectedCategoryId, priceListId }));
    }
  }, [selectedCategoryId, dispatch]);

  useEffect(() => {
    if (priceListId === null && defaultPriceListId !== null) setPriceListId(defaultPriceListId);
  }, [defaultPriceListId]);

  // Switching lists reloads prices without resetting the current selection
  useEffect(() => {
    if (selectedCategoryId !== null && selectedCategoryId !== -100000 && priceListId !== null) {
      dispatch(getPricing({ id: selectedCategoryId, priceListId }));
    }
  }, [priceListId]);

  const isAlteration = selectedCategoryId === -100000;
  const subcategories: SubcategoryResponse[] = isAlteration
    ? (alterationSubCategoryResponse?.body as unknown as SubcategoryResponse[]) ?? []
    : subCategoryResponse?.body ?? [];

  // Sync ordered subcategories when Redux data changes
  useEffect(() => {
    setOrderedSubcategories(subcategories);
  }, [subCategoryResponse, alterationSubCategoryResponse]);

  // Derive + sync materials for selected category
  const materialsForCategory: CategoryMaterialRelationshipResponse[] =
    selectedCategoryId !== null && selectedCategoryId !== -100000
      ? (categoryMaterialRelationship?.body ?? []).filter(
          (m) => m.categoryId === selectedCategoryId
        )
      : [];

  useEffect(() => {
    setOrderedMaterials(materialsForCategory);
  }, [categoryMaterialRelationship, selectedCategoryId]);

  // Derive + sync unique second subcategories (sub types) for selected category + garment type
  const subTypesForSelection: CategoryRelationshipResponse[] =
    selectedCategoryId !== null && selectedCategoryId !== -100000 && selectedSubcategoryId !== null
      ? (categoryRelationshipResponse?.body ?? []).filter(
          (r) => r.categoryId === selectedCategoryId && r.subCategoryId === selectedSubcategoryId
        ).filter((v, i, a) => a.findIndex((t) => t.secondSubCategoryId === v.secondSubCategoryId) === i)
      : [];

  useEffect(() => {
    setSelectedMaterialId(null);
    setOrderedSubTypes(subTypesForSelection);
  }, [categoryRelationshipResponse, selectedCategoryId, selectedSubcategoryId]);

  const pricings = dryCleanResponse?.body ?? [];

  const hasPrice = (subcategoryId: number): boolean => {
    if (isAlteration) return true;
    return pricings.some((p) => p.subCategoryId === subcategoryId);
  };

  const subcategoriesLoading = isAlteration
    ? alterationSubCategoryResponseLoading
    : subCategoryResponseLoading || dryCleanResponseLoading;

  const blocker = useBlocker(hasChanges);

  // When blocker fires (router-level navigation), show confirm modal
  useEffect(() => {
    if (blocker.state === "blocked") {
      setNavConfirmOpen(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    setUsage(null);
    if (selectedCategoryId === null || selectedCategoryId === -100000) return;
    let cancelled = false;
    axios
      .get(GET_MENU_USAGE(selectedCategoryId))
      .then((res) => {
        if (!cancelled) setUsage(res.data?.body ?? null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId]);

  // "A–Z" / "Most used": rearrange a list once (drag still works), then Save
  const applySort = <T,>(
    list: T[],
    setList: (items: T[]) => void,
    mode: "name" | "usage",
    name: (item: T) => string,
    uses: (item: T) => number
  ) => {
    if (list.length < 2) return;
    if (mode === "usage" && hasNoUsage(list, uses)) {
      snackbar.showSnackbar("None of these were used on bills in the last 90 days.", "info");
      return;
    }
    setList(mode === "name" ? sortByName(list, name) : sortByUsage(list, uses));
    setHasChanges(true);
  };
  const sortGarments = (mode: "name" | "usage") =>
    applySort(orderedSubcategories, setOrderedSubcategories, mode,
      (g) => g.subcategoryName, (g) => usage?.garments?.[String(g.subcategoryId)] ?? 0);
  const sortSubTypes = (mode: "name" | "usage") =>
    applySort(orderedSubTypes, setOrderedSubTypes, mode,
      (st) => st.secondSubCategoryName,
      (st) => usage?.styles?.[`${selectedSubcategoryId}:${st.secondSubCategoryId}`] ?? 0);
  const sortMaterials = (mode: "name" | "usage") =>
    applySort(orderedMaterials, setOrderedMaterials, mode,
      (m) => m.materialName, (m) => usage?.fabrics?.[String(m.materialId)] ?? 0);

  const moveCategory = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedCategories((prev) =>
      update(prev, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, prev[dragIndex]]],
      })
    );
    setHasChanges(true);
  }, []);

  const moveSubType = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedSubTypes((prev) =>
      update(prev, { $splice: [[dragIndex, 1], [hoverIndex, 0, prev[dragIndex]]] })
    );
    setHasChanges(true);
  }, []);

  const moveMaterial = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedMaterials((prev) =>
      update(prev, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, prev[dragIndex]]],
      })
    );
    setHasChanges(true);
  }, []);

  const moveSubcategory = useCallback((dragIndex: number, hoverIndex: number) => {
    setOrderedSubcategories((prev) =>
      update(prev, {
        $splice: [[dragIndex, 1], [hoverIndex, 0, prev[dragIndex]]],
      })
    );
    setHasChanges(true);
  }, []);

  const handleUpdate = async () => {
    try {
      await Promise.all([
        handleSaveServiceOrder(),
        handleSaveGarmentOrder(),
        handleSaveMaterialOrder(),
        handleSaveSubTypeOrder(),
      ]);
      setHasChanges(false);
    } catch {
      // individual handlers already show snackbar on error
    }
  };

  const handleSaveServiceOrder = async () => {
    const payload = orderedCategories
      .filter((c) => c.id !== -100000)
      .map((cat) => ({ categoryId: cat.id }));
    try {
      await dispatch(reorderCategoriesAction(payload)).unwrap();
      snackbar.showSnackbar("Service order saved!", "success");
    } catch {
      snackbar.showSnackbar("Failed to save service order.", "error");
    }
  };

  const handleCreateGarmentType = async () => {
    if (!newGarmentName.trim() || !newGarmentFile || selectedCategoryId === null || isAlteration) return;
    try {
      await dispatch(
        createSubcategoryWithImageAction({
          name: newGarmentName.trim(),
          categoryId: selectedCategoryId,
          file: newGarmentFile,
        })
      ).unwrap();
      snackbar.showSnackbar("Garment type created!", "success");
      setAddGarmentModalOpen(false);
      setNewGarmentName("");
      setNewGarmentFile(null);
      dispatch(getSubCategoryAction({ id: selectedCategoryId }));
    } catch {
      snackbar.showSnackbar("Failed to create garment type.", "error");
    }
  };

  const handleCreateMaterial = async () => {
    if (!newMaterialName.trim() || selectedCategoryId === null || isAlteration) return;
    try {
      await dispatch(
        createMaterialAction({ name: newMaterialName.trim(), categoryId: selectedCategoryId })
      ).unwrap();
      snackbar.showSnackbar("Material created!", "success");
      setAddMaterialModalOpen(false);
      setNewMaterialName("");
      dispatch(getCategoryMaterialRelationship());
    } catch {
      snackbar.showSnackbar("Failed to create material.", "error");
    }
  };

  const handleSaveMaterialOrder = async () => {
    if (isAlteration || selectedCategoryId === null) return;
    // This service's own order: other services sharing a fabric keep theirs
    const payload = orderedMaterials.map((m) => ({ materialId: m.materialId, categoryId: selectedCategoryId }));
    try {
      await dispatch(reorderMaterialsAction(payload)).unwrap();
      snackbar.showSnackbar("Material order saved!", "success");
    } catch {
      snackbar.showSnackbar("Failed to save material order.", "error");
    }
  };

  const handleSaveGarmentOrder = async () => {
    if (isAlteration || selectedCategoryId === null) return;
    const payload = orderedSubcategories.map((sub) => ({
      id: sub.subcategoryId,
      categoryId: sub.categoryId,
    }));
    try {
      await dispatch(reorderSubCategoriesAction(payload)).unwrap();
      snackbar.showSnackbar("Garment type order saved!", "success");
    } catch {
      snackbar.showSnackbar("Failed to save garment type order.", "error");
    }
  };

  const handleCreateSubType = async () => {
    if (!newSubTypeName.trim() || selectedCategoryId === null || selectedSubcategoryId === null || selectedMaterialId === null || isAlteration) return;
    try {
      const parsedPieceCount = parseInt(newSubTypePieceCount, 10);
      const result = await dispatch(
        createSecondSubcategoryAction({
          name: newSubTypeName.trim(),
          pieceCount: Number.isFinite(parsedPieceCount) && parsedPieceCount >= 0 ? parsedPieceCount : 1,
          categoryId: selectedCategoryId,
          subcategoryId: selectedSubcategoryId,
        })
      ).unwrap();

      const newSecondSubCategoryId = (result.response?.body as any)?.id;
      const parsedPrice = parseFloat(newSubTypePrice);
      if (newSecondSubCategoryId && newSubTypePrice.trim() !== "" && !isNaN(parsedPrice)) {
        await dispatch(
          upsertPriceAction({
            priceListId,
            categoryId: selectedCategoryId,
            subCategoryId: selectedSubcategoryId,
            secondSubCategoryId: newSecondSubCategoryId,
            materialId: selectedMaterialId,
            price: parsedPrice,
          })
        ).unwrap();
      }

      snackbar.showSnackbar("Sub type created!", "success");
      setAddSubTypeModalOpen(false);
      setNewSubTypeName("");
      setNewSubTypePrice("");
      setNewSubTypePieceCount("1");
      dispatch(getCategoryRelationship());
      dispatch(getPricing({ id: selectedCategoryId, priceListId }));
    } catch {
      snackbar.showSnackbar("Failed to create sub type.", "error");
    }
  };

  const handleSaveSubTypeOrder = async () => {
    if (isAlteration || selectedCategoryId === null || selectedSubcategoryId === null) return;
    // This service + garment's own order: the same style elsewhere keeps its place
    const payload = orderedSubTypes.map((s) => ({
      id: s.secondSubCategoryId,
      categoryId: selectedCategoryId,
      subcategoryId: selectedSubcategoryId,
    }));
    try {
      await dispatch(reorderSecondSubcategoriesAction(payload)).unwrap();
      snackbar.showSnackbar("Sub type order saved!", "success");
    } catch {
      snackbar.showSnackbar("Failed to save sub type order.", "error");
    }
  };

  const handleUpdateService = async () => {
    if (!editService || !editService.name.trim()) return;
    try {
      await dispatch(
        patchCategoryAction({
          id: editService.id,
          categoryName: editService.name.trim(),
          categoryCode: editService.code.trim() || null,
          showInMenu: editService.showInMenu,
          alterationCategoryId: editService.alterationCategoryId,
          maxItemsPerBill: editServiceMaxItems === "" ? null : Number(editServiceMaxItems),
          allowZeroPrice: editService.allowZeroPrice,
        })
      ).unwrap();
      snackbar.showSnackbar("Service updated!", "success");
      setEditService(null);
      dispatch(getCategoryAction());
    } catch (err: any) {
      snackbar.showSnackbar(typeof err === "string" ? err : "Failed to update service.", "error");
    }
  };

  const handleUpdateGarment = async () => {
    if (!editGarment || !editGarment.name.trim()) return;
    try {
      await dispatch(updateSubcategoryAction({ id: editGarment.id, name: editGarment.name.trim(), categoryId: editGarment.categoryId })).unwrap();
      snackbar.showSnackbar("Garment type updated!", "success");
      setEditGarment(null);
      if (selectedCategoryId !== null) dispatch(getSubCategoryAction({ id: selectedCategoryId }));
    } catch {
      snackbar.showSnackbar("Failed to update garment type.", "error");
    }
  };

  const handleUpdateMaterial = async () => {
    if (!editMaterial || !editMaterial.name.trim()) return;
    try {
      await dispatch(updateMaterialAction({ id: editMaterial.id, name: editMaterial.name.trim() })).unwrap();
      snackbar.showSnackbar("Material updated!", "success");
      setEditMaterial(null);
      dispatch(getCategoryMaterialRelationship());
    } catch {
      snackbar.showSnackbar("Failed to update material.", "error");
    }
  };

  const handleUpdateSubType = async () => {
    if (!editSubType || !editSubType.name.trim() || selectedCategoryId === null || selectedSubcategoryId === null || selectedMaterialId === null) return;
    try {
      const parsedPieceCount = parseInt(editSubType.pieceCount, 10);
      await dispatch(updateSecondSubcategoryAction({
        sub2Id: editSubType.id,
        sub2Name: editSubType.name.trim(),
        pieceCount: Number.isFinite(parsedPieceCount) && parsedPieceCount >= 0 ? parsedPieceCount : 1,
      })).unwrap();

      const parsedPrice = parseFloat(editSubType.price);
      if (editSubType.price.trim() !== "" && !isNaN(parsedPrice)) {
        await dispatch(
          upsertPriceAction({
            priceListId,
            categoryId: selectedCategoryId,
            subCategoryId: selectedSubcategoryId,
            secondSubCategoryId: editSubType.id,
            materialId: selectedMaterialId,
            price: parsedPrice,
          })
        ).unwrap();
      }

      snackbar.showSnackbar("Sub type updated!", "success");
      setEditSubType(null);
      dispatch(getCategoryRelationship());
      dispatch(getPricing({ id: selectedCategoryId, priceListId }));
    } catch {
      snackbar.showSnackbar("Failed to update sub type.", "error");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    const { type, id } = deleteConfirm;
    try {
      if (type === "service") {
        await dispatch(deleteCategoryAction(id)).unwrap();
        dispatch(getCategoryAction());
        if (selectedCategoryId === id) setSelectedCategoryId(null);
      } else if (type === "garment") {
        await dispatch(deleteSubcategoryAction(id)).unwrap();
        if (selectedCategoryId !== null) dispatch(getSubCategoryAction({ id: selectedCategoryId }));
        if (selectedSubcategoryId === id) setSelectedSubcategoryId(null);
      } else if (type === "material") {
        await dispatch(deleteMaterialAction(id)).unwrap();
        dispatch(getCategoryMaterialRelationship());
        if (selectedMaterialId === id) setSelectedMaterialId(null);
      } else if (type === "subtype") {
        await dispatch(deleteSecondSubcategoryAction(id)).unwrap();
        dispatch(getCategoryRelationship());
      }
      snackbar.showSnackbar("Deleted successfully!", "success");
    } catch {
      snackbar.showSnackbar("Failed to delete.", "error");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getSubTypePriceRow = (secondSubCategoryId: number) => {
    if (selectedSubcategoryId === null || selectedMaterialId === null) return undefined;
    return pricings.find(
      (p) =>
        p.categoryId === selectedCategoryId &&
        p.subCategoryId === selectedSubcategoryId &&
        p.secondSubCategoryId === secondSubCategoryId &&
        p.materialId === selectedMaterialId
    );
  };

  const getSubTypePrice = (secondSubCategoryId: number): number | null => {
    const match = getSubTypePriceRow(secondSubCategoryId);
    return match != null ? match.price : null;
  };

  // Price shown comes from the default list, not the list being edited
  const isInheritedPrice = (secondSubCategoryId: number): boolean => {
    const match = getSubTypePriceRow(secondSubCategoryId);
    return !editingDefaultList && match != null && match.priceListId !== priceListId;
  };

  const handleResetSubTypePrice = async () => {
    if (!editSubType || priceListId === null || editingDefaultList) return;
    if (selectedCategoryId === null || selectedSubcategoryId === null || selectedMaterialId === null) return;
    try {
      await dispatch(
        resetListPriceAction({
          priceListId,
          categoryId: selectedCategoryId,
          subCategoryId: selectedSubcategoryId,
          secondSubCategoryId: editSubType.id,
          materialId: selectedMaterialId,
        })
      ).unwrap();
      snackbar.showSnackbar("Price reset to the default list's price.", "success");
      setEditSubType(null);
      dispatch(getPricing({ id: selectedCategoryId, priceListId }));
    } catch {
      snackbar.showSnackbar("Failed to reset price.", "error");
    }
  };

  const canAddSubType = !isAlteration && selectedSubcategoryId !== null && selectedMaterialId !== null;

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 4.357rem)",
          py: "1.5rem",
          paddingInline: "2rem",
          width: "100%",
          gap: "1.25rem",
          boxSizing: "border-box",
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IconButton
            onClick={() => {
              if (hasChanges) {
                setNavConfirmOpen(true);
              } else {
                navigate(-1);
              }
            }}
            size="small"
          >
            <ChevronLeftIcon sx={{ fontSize: "1.8rem", color: "#515151" }} />
          </IconButton>
          <Typography
            variant="h5"
            sx={{ color: "#515151", fontWeight: 700, fontSize: "1.5rem", flex: 1 }}
          >
            Service Setup
          </Typography>
          {!isAlteration && (
            <PriceListSelect
              value={priceListId}
              onChange={(id) => id !== null && setPriceListId(id)}
              label="Editing price list"
            />
          )}

          {/* Upload button */}
          <Tooltip title="Import">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2.6rem",
                height: "2.6rem",
                border: `1px solid ${Colors.PRIMARY}`,
                borderRadius: "0.5rem",
                cursor: "pointer",
                "&:hover": { background: "#e8f5ef" },
              }}
            >
              <FileUploadOutlinedIcon sx={{ color: Colors.PRIMARY, fontSize: "1.3rem" }} />
            </Box>
          </Tooltip>

          {/* Download button */}
          <Tooltip title="Export">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2.6rem",
                height: "2.6rem",
                border: `1px solid ${Colors.PRIMARY}`,
                borderRadius: "0.5rem",
                cursor: "pointer",
                "&:hover": { background: "#e8f5ef" },
              }}
            >
              <FileDownloadOutlinedIcon sx={{ color: Colors.PRIMARY, fontSize: "1.3rem" }} />
            </Box>
          </Tooltip>

          {/* Update button */}
          <CustomButton
            disabled={!hasChanges}
            onClick={handleUpdate}
            sx={{
              height: "2.6rem",
              px: "1.5rem",
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: "0.5rem",
            }}
          >
            Update
          </CustomButton>
        </Box>

        {/* ── Four-column layout ──────────────────────────────────────────────── */}
        <DndProvider backend={HTML5Backend}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              backgroundColor: Colors.WHITE,
              borderRadius: "1rem",
              boxShadow: "0 0.1rem 0.5rem rgba(0,0,0,0.08)",
              p: "1.25rem",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {/* ── Service column ─────────────────────────────────────────────── */}
            <Column
              title="Service"
              loading={categoryResponseLoading}
            >
              {orderedCategories.map((cat: any, index: number) => (
                <DraggableServiceButton
                  key={cat.id}
                  id={cat.id}
                  index={index}
                  label={cat.id !== -100000 && !cat.showInMenu ? `${cat.categoryName} (hidden)` : cat.categoryName}
                  selected={selectedCategoryId === cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  onEdit={() =>
                    cat.id !== -100000 &&
                    setEditService({
                      id: cat.id,
                      name: cat.categoryName,
                      code: cat.categoryCode ?? "",
                      showInMenu: Boolean(cat.showInMenu),
                      alterationCategoryId: cat.alterationCategoryId ?? null,
                      maxItemsPerBill: cat.maxItemsPerBill ? String(cat.maxItemsPerBill) : "",
                      allowZeroPrice: Boolean(cat.allowZeroPrice),
                    })
                  }
                  onDelete={() => setDeleteConfirm({ type: "service", id: cat.id, name: cat.categoryName })}
                  moveItem={moveCategory}
                />
              ))}
            </Column>

            {/* ── Garment Type column ────────────────────────────────────────── */}
            <Column
              title="Garment Type"
              loading={subcategoriesLoading}
              onAdd={() => setAddGarmentModalOpen(true)}
              addDisabled={isAlteration}
              onSortAZ={() => sortGarments("name")}
              onSortMostUsed={() => sortGarments("usage")}
              sortDisabled={isAlteration || orderedSubcategories.length < 2}
            >
              {orderedSubcategories.map((sub, index) => (
                <DraggableItemRow
                  key={sub.subcategoryId}
                  id={sub.subcategoryId}
                  index={index}
                  label={sub.subcategoryName}
                  selected={selectedSubcategoryId === sub.subcategoryId}
                  toggleOn={hasPrice(sub.subcategoryId)}
                  price={null}
                  showPrice={false}
                  itemType={ITEM_TYPE_GARMENT}
                  onClick={() => setSelectedSubcategoryId(sub.subcategoryId)}
                  onEdit={() => setEditGarment({ id: sub.subcategoryId, name: sub.subcategoryName, categoryId: sub.categoryId })}
                  onDelete={() => setDeleteConfirm({ type: "garment", id: sub.subcategoryId, name: sub.subcategoryName })}
                  moveItem={moveSubcategory}
                />
              ))}
            </Column>

            {/* ── Sub Type column ────────────────────────────────────────────── */}
            <Column
              title="Sub Type"
              onAdd={() => setAddSubTypeModalOpen(true)}
              addDisabled={!canAddSubType}
              onSortAZ={() => sortSubTypes("name")}
              onSortMostUsed={() => sortSubTypes("usage")}
              sortDisabled={isAlteration || orderedSubTypes.length < 2}
            >
              {orderedSubTypes.map((st, index) => {
                const price = getSubTypePrice(st.secondSubCategoryId);
                return (
                  <DraggableItemRow
                    key={st.secondSubCategoryId}
                    id={st.secondSubCategoryId}
                    index={index}
                    label={
                      isInheritedPrice(st.secondSubCategoryId)
                        ? `${st.secondSubCategoryName} (default price)`
                        : st.secondSubCategoryName
                    }
                    selected={false}
                    toggleOn={price !== null && price > 0}
                    price={price}
                    showPrice={true}
                    itemType={ITEM_TYPE_SUBTYPE}
                    onClick={() => {}}
                    onEdit={() => {
                      setEditSubType({
                        id: st.secondSubCategoryId,
                        name: st.secondSubCategoryName,
                        price: price !== null ? String(price) : "",
                        pieceCount: String(st.pieceCount ?? 1),
                      });
                    }}
                    onDelete={() => setDeleteConfirm({ type: "subtype", id: st.secondSubCategoryId, name: st.secondSubCategoryName })}
                    moveItem={moveSubType}
                  />
                );
              })}
            </Column>

            {/* ── Material column ────────────────────────────────────────────── */}
            <Column
              title="Material"
              loading={categoryMaterialRelationshipLoading}
              onAdd={() => setAddMaterialModalOpen(true)}
              addDisabled={isAlteration}
              onSortAZ={() => sortMaterials("name")}
              onSortMostUsed={() => sortMaterials("usage")}
              sortDisabled={isAlteration || orderedMaterials.length < 2}
            >
              {orderedMaterials.map((mat, index) => (
                <DraggableItemRow
                  key={mat.materialId}
                  id={mat.materialId}
                  index={index}
                  label={mat.materialName}
                  selected={selectedMaterialId === mat.materialId}
                  toggleOn={true}
                  price={null}
                  showPrice={false}
                  itemType={ITEM_TYPE_MATERIAL}
                  onClick={() => setSelectedMaterialId(mat.materialId)}
                  onEdit={() => setEditMaterial({ id: mat.materialId, name: mat.materialName })}
                  onDelete={() => setDeleteConfirm({ type: "material", id: mat.materialId, name: mat.materialName })}
                  moveItem={moveMaterial}
                />
              ))}
            </Column>
          </Box>
        </DndProvider>
      </Box>

      {/* ── Add Garment Type Modal ──────────────────────────────────────────── */}
      <CustomModal
        title="Add Garment Type"
        openModal={addGarmentModalOpen}
        onClose={() => {
          setAddGarmentModalOpen(false);
          setNewGarmentName("");
          setNewGarmentFile(null);
        }}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={newGarmentName}
            onChange={(e) => setNewGarmentName(e.target.value)}
            fullWidth
            size="small"
          />
          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: "0.5rem",
              px: "1rem",
              py: "0.75rem",
              cursor: "pointer",
              "&:hover": { borderColor: "text.secondary" },
            }}
          >
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setNewGarmentFile(e.target.files?.[0] ?? null)}
            />
            <UploadFileIcon sx={{ color: "text.secondary" }} />
            <Typography sx={{ fontSize: "0.95rem", color: newGarmentFile ? "text.primary" : "text.secondary" }}>
              {newGarmentFile ? newGarmentFile.name : "Choose image…"}
            </Typography>
          </Box>
          <CustomButton
            onClick={handleCreateGarmentType}
            disabled={createSubcategoryLoading || !newGarmentName.trim() || !newGarmentFile}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {createSubcategoryLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Add Material Modal ──────────────────────────────────────────────── */}
      <CustomModal
        title="Add Material"
        openModal={addMaterialModalOpen}
        onClose={() => {
          setAddMaterialModalOpen(false);
          setNewMaterialName("");
        }}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={newMaterialName}
            onChange={(e) => setNewMaterialName(e.target.value)}
            fullWidth
            size="small"
          />
          <CustomButton
            onClick={handleCreateMaterial}
            disabled={createMaterialLoading || !newMaterialName.trim()}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {createMaterialLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Delete Confirmation Modal ───────────────────────────────────────── */}
      <CustomModal
        title="Delete"
        openModal={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        width="26rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", mt: "0.5rem" }}>
          <Typography sx={{ fontSize: "1rem", color: Colors.TEXT_GRAY_DARK }}>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
          </Typography>
          <Box sx={{ display: "flex", gap: "0.75rem" }}>
            <CustomButton
              onClick={() => setDeleteConfirm(null)}
              sx={{ flex: 1, height: "3.2rem", fontSize: "1rem", background: Colors.ACCENT_GRAY_DISABLED, color: Colors.TEXT_GRAY_DARK }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleConfirmDelete}
              disabled={deleteCategoryLoading || deleteSubcategoryLoading || deleteMaterialLoading || deleteSecondSubcategoryLoading}
              sx={{ flex: 1, height: "3.2rem", fontSize: "1rem", background: "#d32f2f", "&:hover": { background: "#b71c1c" } }}
            >
              {deleteCategoryLoading || deleteSubcategoryLoading || deleteMaterialLoading || deleteSecondSubcategoryLoading
                ? <CircularProgress size={20} color="inherit" />
                : "Delete"}
            </CustomButton>
          </Box>
        </Box>
      </CustomModal>

      {/* ── Navigate Away Confirmation Modal ───────────────────────────────── */}
      <CustomModal
        title="Unsaved Changes"
        openModal={navConfirmOpen}
        onClose={() => {
          setNavConfirmOpen(false);
          if (blocker.state === "blocked") blocker.reset?.();
        }}
        width="26rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", mt: "0.5rem" }}>
          <Typography sx={{ fontSize: "1rem", color: Colors.TEXT_GRAY_DARK }}>
            You have unsaved order changes. Are you sure you want to leave?
          </Typography>
          <Box sx={{ display: "flex", gap: "0.75rem" }}>
            <CustomButton
              onClick={() => {
                setNavConfirmOpen(false);
                if (blocker.state === "blocked") blocker.reset?.();
              }}
              sx={{ flex: 1, height: "3.2rem", fontSize: "1rem", background: Colors.ACCENT_GRAY_DISABLED, color: Colors.TEXT_GRAY_DARK }}
            >
              Stay
            </CustomButton>
            <CustomButton
              onClick={() => {
                setNavConfirmOpen(false);
                setHasChanges(false);
                if (blocker.state === "blocked") {
                  blocker.proceed?.();
                } else {
                  navigate(-1);
                }
              }}
              sx={{ flex: 1, height: "3.2rem", fontSize: "1rem" }}
            >
              Leave
            </CustomButton>
          </Box>
        </Box>
      </CustomModal>

      {/* ── Edit Service Modal ──────────────────────────────────────────────── */}
      <CustomModal
        title="Edit Service"
        openModal={editService !== null}
        onClose={() => setEditService(null)}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={editService?.name ?? ""}
            onChange={(e) => setEditService((prev) => prev ? { ...prev, name: e.target.value } : prev)}
            fullWidth
            size="small"
          />
          <TextField
            label="Invoice code"
            value={editService?.code ?? ""}
            onChange={(e) => setEditService((prev) => prev ? { ...prev, code: e.target.value.toUpperCase() } : prev)}
            fullWidth
            size="small"
            placeholder="e.g. CR"
            helperText="Prefix for this service's invoice numbers."
            inputProps={{ maxLength: 10 }}
          />
          <TextField
            select
            label="Alteration prices"
            value={editService?.alterationCategoryId ?? ""}
            onChange={(e) =>
              setEditService((prev) =>
                prev ? { ...prev, alterationCategoryId: e.target.value === "" ? null : Number(e.target.value) } : prev
              )
            }
            fullWidth
            size="small"
            helperText="Offers the Alteration tab for this service, priced from this category."
          >
            <MenuItem value="">No alterations</MenuItem>
            {alterationCategoryOptions.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.categoryName}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Max pieces per bill"
            value={editService?.maxItemsPerBill ?? ""}
            onChange={(e) =>
              setEditService((prev) => (prev ? { ...prev, maxItemsPerBill: e.target.value.replace(/[^\d]/g, "") } : prev))
            }
            fullWidth
            size="small"
            placeholder={defaultMaxItemsPerBill ? `Default (${defaultMaxItemsPerBill})` : "No limit"}
            error={editServiceMaxItemsInvalid}
            helperText={
              editServiceMaxItemsInvalid
                ? "Enter a whole number of at least 1, or leave blank."
                : defaultMaxItemsPerBill
                  ? `Leave blank to use the default of ${defaultMaxItemsPerBill} (Config → Merchants).`
                  : "Leave blank for no limit, or set a default in Config → Merchants."
            }
            inputProps={{ inputMode: "numeric", maxLength: 4 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editService?.showInMenu ?? false}
                onChange={(e) => setEditService((prev) => prev ? { ...prev, showInMenu: e.target.checked } : prev)}
              />
            }
            label={<Typography sx={{ fontSize: "var(--fs-2xs)" }}>Show in POS</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editService?.allowZeroPrice ?? false}
                onChange={(e) => setEditService((prev) => (prev ? { ...prev, allowZeroPrice: e.target.checked } : prev))}
              />
            }
            label={
              <Typography sx={{ fontSize: "var(--fs-2xs)" }}>
                Offer $0 items in POS
                <Typography component="span" sx={{ display: "block", fontSize: "var(--fs-2xs)", color: "text.secondary" }}>
                  Off: $0 prices are treated as unpriced and hidden.
                </Typography>
              </Typography>
            }
          />
          {editService?.showInMenu && !editService.code.trim() && (
            <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#b26a00" }}>
              Add an invoice code before showing this service in the POS.
            </Typography>
          )}
          <CustomButton
            onClick={handleUpdateService}
            disabled={
              updateCategoryLoading ||
              editServiceMaxItemsInvalid ||
              !editService?.name.trim() ||
              (editService.showInMenu && !editService.code.trim())
            }
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {updateCategoryLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Edit Garment Type Modal ─────────────────────────────────────────── */}
      <CustomModal
        title="Edit Garment Type"
        openModal={editGarment !== null}
        onClose={() => setEditGarment(null)}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={editGarment?.name ?? ""}
            onChange={(e) => setEditGarment((prev) => prev ? { ...prev, name: e.target.value } : prev)}
            fullWidth
            size="small"
          />
          <CustomButton
            onClick={handleUpdateGarment}
            disabled={updateSubcategoryLoading || !editGarment?.name.trim()}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {updateSubcategoryLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Edit Material Modal ─────────────────────────────────────────────── */}
      <CustomModal
        title="Edit Material"
        openModal={editMaterial !== null}
        onClose={() => setEditMaterial(null)}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={editMaterial?.name ?? ""}
            onChange={(e) => setEditMaterial((prev) => prev ? { ...prev, name: e.target.value } : prev)}
            fullWidth
            size="small"
          />
          <CustomButton
            onClick={handleUpdateMaterial}
            disabled={updateMaterialLoading || !editMaterial?.name.trim()}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {updateMaterialLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Edit Sub Type Modal ─────────────────────────────────────────────── */}
      <CustomModal
        title="Edit Sub Type"
        openModal={editSubType !== null}
        onClose={() => setEditSubType(null)}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={editSubType?.name ?? ""}
            onChange={(e) => setEditSubType((prev) => prev ? { ...prev, name: e.target.value } : prev)}
            fullWidth
            size="small"
          />
          <TextField
            label="Price"
            type="number"
            value={editSubType?.price ?? ""}
            onChange={(e) => setEditSubType((prev) => prev ? { ...prev, price: e.target.value } : prev)}
            fullWidth
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Piece Count"
            type="number"
            value={editSubType?.pieceCount ?? "1"}
            onChange={(e) => setEditSubType((prev) => prev ? { ...prev, pieceCount: e.target.value } : prev)}
            fullWidth
            size="small"
            helperText="Physical pieces in one unit (a suit = 2, a pair of shoes = 2). Use 0 for add-ons like repairs."
            inputProps={{ min: 0, step: 1 }}
          />
          {editSubType && !editingDefaultList && !isInheritedPrice(editSubType.id) && getSubTypePriceRow(editSubType.id) && (
            <CustomButton
              onClick={handleResetSubTypePrice}
              sx={{ width: "100%", height: "2.6rem", fontSize: "var(--fs-2xs)", backgroundColor: "#e0e0e0", color: "#515151" }}
            >
              Reset to default list price
            </CustomButton>
          )}
          <CustomButton
            onClick={handleUpdateSubType}
            disabled={updateSecondSubcategoryLoading || upsertPriceLoading || !editSubType?.name.trim()}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {updateSecondSubcategoryLoading || upsertPriceLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>

      {/* ── Add Sub Type Modal ──────────────────────────────────────────────── */}
      <CustomModal
        title="Add Sub Type"
        openModal={addSubTypeModalOpen}
        onClose={() => {
          setAddSubTypeModalOpen(false);
          setNewSubTypeName("");
          setNewSubTypePrice("");
          setNewSubTypePieceCount("1");
        }}
        width="28rem"
        height="auto"
        titleSize="1.3rem"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: "0.5rem" }}>
          <TextField
            label="Name"
            value={newSubTypeName}
            onChange={(e) => setNewSubTypeName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Price"
            type="number"
            value={newSubTypePrice}
            onChange={(e) => setNewSubTypePrice(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Piece Count"
            type="number"
            value={newSubTypePieceCount}
            onChange={(e) => setNewSubTypePieceCount(e.target.value)}
            fullWidth
            size="small"
            helperText="Physical pieces in one unit (a suit = 2, a pair of shoes = 2). Use 0 for add-ons like repairs."
            inputProps={{ min: 0, step: 1 }}
          />
          <CustomButton
            onClick={handleCreateSubType}
            disabled={createSecondSubcategoryLoading || upsertPriceLoading || !newSubTypeName.trim()}
            sx={{ width: "100%", height: "3.2rem", fontSize: "1.1rem" }}
          >
            {createSecondSubcategoryLoading || upsertPriceLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </CustomButton>
        </Box>
      </CustomModal>
    </AdminLayout>
  );
};
